import { useMemo } from 'react';
import { Provider, TextContext } from 'react-aria-components';
import * as Ariakit from '@ariakit/react';
import { Text } from '@launchpad-ui/components';

import { Box } from '../Box/Box';

import styles from './Tooltip.module.css';

const STANDARD_DELAY = 500;

export type TooltipProps = Partial<Ariakit.TooltipStoreProps> &
  React.PropsWithChildren<{
    trigger: React.ReactNode;
    disabled?: boolean;
    style?: React.CSSProperties;
    delayed?: boolean;
    tooltipRef?: React.RefObject<HTMLElement>;
    renderInLine?: boolean; // used when trying to display a tooltip in a modal
    shift?: number;
  }>;

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  trigger,
  disabled,
  style,
  delayed,
  renderInLine,
  shift,
  ...props
}: TooltipProps) => {
  const tooltipStore = Ariakit.useTooltipStore({
    placement: 'top',
    timeout: delayed ? STANDARD_DELAY : 0,
    ...props,
  });

  const portalElement = useMemo(() => document.getElementById('o11y-portal'), []);

  return (
    <>
      <Ariakit.TooltipAnchor store={tooltipStore} style={{ display: 'flex', ...style }}>
        {trigger}
      </Ariakit.TooltipAnchor>
      {!disabled && (
        <Ariakit.Tooltip
          store={tooltipStore}
          gutter={4}
          style={{ zIndex: 660 }} // needed to display over header and panels
          portal={!renderInLine}
          portalElement={portalElement}
          shift={shift}
        >
          {/*
            There is a bug in v0.2.17 of Ariakit where you need to have this arrow
            rendered or else positioning of the popover breaks. We render it, but
            hide it by setting size={0}. This is an issue with anything using a
            popover coming from the floating-ui library.
          */}
          <Ariakit.TooltipArrow size={0} />
          <TooltipContent>{children}</TooltipContent>
        </Ariakit.Tooltip>
      )}
    </>
  );
};

export const TooltipContent: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box cssClass={styles.tooltipContent}>
    {/* Resetting TextContext keeps tooltips working inside components that require a Text slot, such as a Launchpad Dialog. */}
    <Provider values={[[TextContext, {}]]}>
      <Text>{children}</Text>
    </Provider>
  </Box>
);
